/**
 * Inbox model: one queue ranked by leverage (work unblocked × waiting time), grouped by
 * workstream. Several open questions in one workstream become a single "decision session" card
 * (docs/ux › Inbox). Snoozes live on the hub (`POST /api/inbox/:id/snooze`); the `Snoozes` map
 * here only hides an item optimistically until the next `inbox` frame drops it.
 */
import type { InboxItem } from './types.js'

export interface InboxGroup {
  key: string
  projectName: string
  workstreamName: string
  runId: string
  score: number
  items: InboxItem[]
  /** ≥2 questions in the workstream: answer them together. */
  batched: boolean
}

/** Rank = hub score when present, else (blocking ? 2 : 1) · unblocks · log2(2 + waiting min). */
export function leverage(item: InboxItem, now: number): number {
  if (Number.isFinite(item.score) && item.score > 0) return item.score
  const waitingMin = Math.max(0, (now - item.createdAt) / 60_000)
  return (item.blocking ? 2 : 1) * Math.max(1, item.unblocks ?? 1) * Math.log2(2 + waitingMin)
}

export function groupInbox(items: InboxItem[], now: number, snoozes: Snoozes = {}): InboxGroup[] {
  const groups = new Map<string, InboxGroup>()
  for (const it of items) {
    if (isSnoozed(snoozes, it.id, now)) continue
    const key = it.workstreamId ?? (it.runId || it.id)
    let g = groups.get(key)
    if (!g) {
      g = {
        key,
        projectName: it.projectName ?? 'Project',
        workstreamName: it.workstreamName ?? it.title,
        runId: it.runId,
        score: 0,
        items: [],
        batched: false,
      }
      groups.set(key, g)
    }
    g.items.push(it)
    g.score = Math.max(g.score, leverage(it, now))
  }
  const out = [...groups.values()]
  for (const g of out) {
    g.items.sort((a, b) => leverage(b, now) - leverage(a, now) || a.createdAt - b.createdAt)
    g.batched = g.items.filter((i) => i.type === 'question').length >= 2
  }
  return out.sort(
    (a, b) =>
      b.score - a.score ||
      (a.items[0]?.createdAt ?? 0) - (b.items[0]?.createdAt ?? 0) ||
      a.key.localeCompare(b.key),
  )
}

export function blockingCount(items: InboxItem[], now: number, snoozes: Snoozes = {}): number {
  return items.filter((i) => i.blocking && !isSnoozed(snoozes, i.id, now)).length
}

// ---- snooze ---------------------------------------------------------------------------------

export type Snoozes = Record<string, number>

export function isSnoozed(s: Snoozes, id: string, now: number) {
  const until = s[id]
  return until !== undefined && until > now
}

export const SNOOZE_OPTIONS = [
  { label: '1 hour', ms: 3600_000 },
  { label: '2 hours', ms: 2 * 3600_000 },
  { label: 'Tonight', ms: -1 },
  { label: 'Tomorrow morning', ms: -2 },
] as const

/** Resolve a snooze option to an absolute time (local 18:00 / next 08:00 for the named ones). */
export function snoozeUntil(ms: number, now = new Date()): number {
  if (ms > 0) return now.getTime() + ms
  const d = new Date(now)
  if (ms === -1) {
    d.setHours(18, 0, 0, 0)
    if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1)
    return d.getTime()
  }
  d.setDate(d.getDate() + 1)
  d.setHours(8, 0, 0, 0)
  return d.getTime()
}
